import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import {
  InflowRoute,
  Tag,
  inflowRoutes as inflowApi,
  tags as tagsApi,
} from '../lib/api';
import { formatDate } from '../lib/format';

const CHANNELS = [
  { value: 'threads', label: 'Threads' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'x', label: 'X (Twitter)' },
  { value: 'qr', label: 'QRコード' },
  { value: 'web', label: 'Web' },
  { value: 'other', label: 'その他' },
];

interface EditState {
  id?: number;
  name: string;
  channel: string;
  description: string;
  tag_ids: number[];
  is_active?: boolean;
}

export function InflowRoutesPage() {
  const [routes, setRoutes] = useState<InflowRoute[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [loading, setLoading] = useState(true);
  const [qrData, setQrData] = useState<{ slug: string; dataUrl: string } | null>(null);

  function reload() {
    Promise.all([inflowApi.list(), tagsApi.list()])
      .then(([r, t]) => {
        setRoutes(r.routes);
        setTags(t.tags);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    reload();
  }, []);

  async function handleSave() {
    if (!editing) return;
    if (editing.id === undefined) {
      await inflowApi.create({
        name: editing.name,
        channel: editing.channel,
        description: editing.description || undefined,
        tag_ids: editing.tag_ids,
      });
    } else {
      await inflowApi.update(editing.id, {
        name: editing.name,
        channel: editing.channel,
        description: editing.description || null,
        tag_ids: editing.tag_ids,
        is_active: editing.is_active,
      });
    }
    setEditing(null);
    reload();
  }

  async function handleDelete(id: number) {
    if (!confirm('この経路を削除しますか？登録済み友だちの経路情報も外れます')) return;
    await inflowApi.remove(id);
    reload();
  }

  function routeUrl(slug: string) {
    return `${window.location.origin}/api/r/${slug}`;
  }

  function copyUrl(slug: string) {
    const url = routeUrl(slug);
    navigator.clipboard.writeText(url);
    alert('URLをコピーしました\n' + url);
  }

  async function showQr(slug: string) {
    const url = routeUrl(slug);
    const dataUrl = await QRCode.toDataURL(url, { width: 240, margin: 2, color: { dark: '#2A2A2A', light: '#FFFFFF' } });
    setQrData({ slug, dataUrl });
  }

  const activeRoutes = routes.filter((r) => r.is_active);
  const totalUsers = routes.reduce((acc, r) => acc + r.user_count, 0);
  const topRoute = [...routes].sort((a, b) => b.user_count - a.user_count)[0];

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="page">
        <div className="page-header">
          <div>
            <div className="page-title">流入経路</div>
            <div className="page-desc">
              媒体・キャンペーンごとに専用URLを発行し、どこから友だち登録されたかを記録します。<br />
              発行数は無制限。経路ごとにタグ自動付与もできます。
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() =>
              setEditing({
                name: '',
                channel: 'threads',
                description: '',
                tag_ids: [],
                is_active: true,
              })
            }
          >
            + 新しい経路
          </button>
        </div>

        <div className="info-callout">
          <b>使い方：</b> 作成した経路URLを発信媒体（Threads・Instagramなど）に貼ると、
          そのリンクから登録した友だちは自動的にこの経路として記録されます。
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
          <StatCard label="経路数" value={routes.length} sub={`うちアクティブ ${activeRoutes.length}件`} />
          <StatCard label="累計登録" value={totalUsers} sub="この経路から" />
          <StatCard
            label="最多経路"
            value={topRoute ? topRoute.name : '—'}
            sub={topRoute ? `${topRoute.user_count}件` : ''}
            small
          />
          <StatCard label="登録タグ数" value={tags.length} sub="現在のタグ総数" />
        </div>

        {loading ? (
          <div className="loading">読み込み中...</div>
        ) : (
          <div className="table-card">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '25%' }}>経路名</th>
                  <th>媒体</th>
                  <th>URL</th>
                  <th>自動付与タグ</th>
                  <th style={{ textAlign: 'right' }}>登録数</th>
                  <th style={{ width: 110 }}>作成日</th>
                  <th style={{ width: 100 }}></th>
                </tr>
              </thead>
              <tbody>
                {routes.map((r) => (
                  <tr key={r.id} style={{ opacity: r.is_active ? 1 : 0.5 }}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 3 }}>{r.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-sub)' }}>
                        {r.description || (r.is_active ? '' : '非アクティブ')}
                      </div>
                    </td>
                    <td>
                      <span className={`channel-pill ch-${r.channel}`}>
                        {CHANNELS.find((c) => c.value === r.channel)?.label ?? r.channel}
                      </span>
                    </td>
                    <td>
                      <span className="url-cell">
                        /api/r/{r.url_slug}
                        <button className="copy-btn" onClick={() => copyUrl(r.url_slug)} title="コピー">
                          ⎘
                        </button>
                      </span>
                    </td>
                    <td>
                      {r.tags.length === 0 && (
                        <span style={{ fontSize: 10, color: 'var(--text-mute)' }}>—</span>
                      )}
                      {r.tags.map((t) => (
                        <span key={t.id} className="tag-pill" style={{ marginRight: 4 }}>
                          #{t.name}
                        </span>
                      ))}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 14 }}>
                      {r.user_count}
                    </td>
                    <td style={{ color: 'var(--text-sub)' }}>{formatDate(r.created_at)}</td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="icon-btn-sm"
                          onClick={() => showQr(r.url_slug)}
                          title="QRコードを表示"
                          style={{ fontSize: 10, fontWeight: 600 }}
                        >
                          QR
                        </button>
                        <button
                          className="icon-btn-sm"
                          onClick={() =>
                            setEditing({
                              id: r.id,
                              name: r.name,
                              channel: r.channel,
                              description: r.description ?? '',
                              tag_ids: r.tags.map((t) => t.id),
                              is_active: r.is_active,
                            })
                          }
                          title="編集"
                        >
                          ✎
                        </button>
                        <button
                          className="icon-btn-sm danger"
                          onClick={() => handleDelete(r.id)}
                          title="削除"
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {routes.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty">
                      経路が登録されていません
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {qrData && (
        <div className="modal-backdrop" onClick={() => setQrData(null)}>
          <div className="modal" style={{ width: 300, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">QRコード</div>
            <div className="modal-sub" style={{ marginBottom: 20 }}>
              このQRを読み取るとLINE友だち追加画面に進みます
            </div>
            <img
              src={qrData.dataUrl}
              alt="QR code"
              style={{ width: 200, height: 200, border: '1px solid var(--border)', borderRadius: 8 }}
            />
            <div style={{ marginTop: 12, fontSize: 10, color: 'var(--text-sub)', wordBreak: 'break-all', lineHeight: 1.6 }}>
              {routeUrl(qrData.slug)}
            </div>
            <div className="modal-actions" style={{ justifyContent: 'center' }}>
              <button className="btn btn-secondary" onClick={() => setQrData(null)}>
                閉じる
              </button>
              <a
                className="btn btn-primary"
                href={qrData.dataUrl}
                download={`qr_${qrData.slug}.png`}
              >
                ⬇ 画像を保存
              </a>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">
              {editing.id === undefined ? '新しい経路を作成' : '経路を編集'}
            </div>
            <div className="modal-sub">媒体ごとにURLを発行し、自動付与タグを設定できます</div>

            <div className="field">
              <label className="field-label">経路名</label>
              <input
                className="input"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="例: Threads_4月キャンペーン"
                autoFocus
              />
            </div>

            <div className="field">
              <label className="field-label">媒体</label>
              <select
                className="select"
                value={editing.channel}
                onChange={(e) => setEditing({ ...editing, channel: e.target.value })}
              >
                {CHANNELS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field-label">説明（任意）</label>
              <input
                className="input"
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                placeholder="どこに貼るかなど"
              />
            </div>

            <div className="field">
              <label className="field-label">自動付与タグ（複数可）</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {tags.map((t) => {
                  const active = editing.tag_ids.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      className={`filter-chip ${active ? 'active' : ''}`}
                      onClick={() =>
                        setEditing({
                          ...editing,
                          tag_ids: active
                            ? editing.tag_ids.filter((id) => id !== t.id)
                            : [...editing.tag_ids, t.id],
                        })
                      }
                    >
                      #{t.name}
                    </button>
                  );
                })}
                {tags.length === 0 && (
                  <span style={{ fontSize: 11, color: 'var(--text-mute)' }}>
                    タグがありません。先にタグ管理から追加してください。
                  </span>
                )}
              </div>
            </div>

            {editing.id !== undefined && (
              <div className="field">
                <label className="field-label">
                  <input
                    type="checkbox"
                    checked={editing.is_active ?? true}
                    onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                    style={{ marginRight: 6 }}
                  />
                  アクティブ
                </label>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>
                キャンセル
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!editing.name.trim()}
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  small,
}: {
  label: string;
  value: string | number;
  sub?: string;
  small?: boolean;
}) {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: '16px 18px',
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: 'var(--text-sub)',
          letterSpacing: '0.1em',
          marginBottom: 8,
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: small ? 14 : 24, fontWeight: 600, marginBottom: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--text-mute)' }}>{sub}</div>}
    </div>
  );
}
